import { Controller } from "@hotwired/stimulus"

// category-preview — preview vivo do form de categoria: espelha nome, TIPO
// (despesa/receita), ícone (grade) e cor (bolinha + presets) no chip. Os SVGs dos
// ícones são renderizados server-side em cada célula da grade; o preview CLONA o
// SVG da célula selecionada (nada de SVG no JS). CSP-safe.
//
// Regra do chip: com cor escolhida → fundo ~16% + texto na cor. Sem cor →
// tonaliza por tipo (despesa = negativo/vermelho, receita = positivo/verde), via
// as classes CSS passadas em expenseTone/incomeTone.
//
// Uso: data-controller="category-preview" com:
//   inputs: nameInput, kindInput (radios), picker
//   hidden: iconValue, colorValue
//   saída:  chip, nameOut, subOut
//   grade:  iconCell (data-icon), swatch (data-color)
//   values: nameFallback, expenseLabel, incomeLabel, expenseTone, incomeTone
export default class extends Controller {
  static targets = [
    "nameInput", "kindInput", "picker", "iconValue", "colorValue",
    "chip", "nameOut", "subOut", "iconCell", "swatch"
  ]
  static values = {
    nameFallback: String,
    expenseLabel: String,
    incomeLabel:  String,
    expenseTone:  String,  // classes ex.: "bg-negative/12 text-negative"
    incomeTone:   String
  }

  connect() {
    this._icon = (this.hasIconValueTarget && this.iconValueTarget.value.trim()) || this._checkedIconName()
    this._color = (this.hasColorValueTarget && this.colorValueTarget.value.trim()) || ""
    this.render()
    this._renderChip()
    this._syncCells()
    this._syncSwatches()
  }

  render() {
    if (this.hasNameOutTarget) {
      const v = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
      this.nameOutTarget.textContent = v || this.nameFallbackValue || ""
    }
    if (this.hasSubOutTarget) {
      this.subOutTarget.textContent = this._isIncome() ? this.incomeLabelValue : this.expenseLabelValue
    }
  }

  // Radio de tipo mudou → atualiza sub-texto E a tonalização do chip (se sem cor).
  changeKind() {
    this.render()
    this._renderChip()
  }

  pickIcon(event) {
    this._icon = event.currentTarget.dataset.icon
    if (this.hasIconValueTarget) this.iconValueTarget.value = this._icon
    this._renderChip()
    this._syncCells()
  }

  pickColor() {
    this._color = this.pickerTarget.value
    this._commitColor()
  }

  presetColor(event) {
    const c = event.currentTarget.dataset.color
    if (!c) return
    this._color = c
    if (this.hasPickerTarget) this.pickerTarget.value = c
    this._commitColor()
  }

  clearColor() {
    this._color = ""
    this._commitColor()
  }

  _commitColor() {
    if (this.hasColorValueTarget) this.colorValueTarget.value = this._color
    this._renderChip()
    this._syncSwatches()
  }

  // ---- chip ----
  _renderChip() {
    if (!this.hasChipTarget) return
    const cell = this._cellFor(this._icon)
    const svg = cell && cell.querySelector("svg")
    if (svg) this.chipTarget.innerHTML = svg.outerHTML

    // Limpa tonalizações antigas por classe.
    const tones = `${this.expenseToneValue} ${this.incomeToneValue}`.split(/\s+/).filter(Boolean)
    tones.forEach((c) => this.chipTarget.classList.remove(c))

    if (this._color) {
      this.chipTarget.style.color = this._color
      this.chipTarget.style.background = `color-mix(in oklch, ${this._color} 16%, transparent)`
    } else {
      this.chipTarget.style.color = ""
      this.chipTarget.style.background = ""
      const tone = (this._isIncome() ? this.incomeToneValue : this.expenseToneValue).split(/\s+/).filter(Boolean)
      tone.forEach((c) => this.chipTarget.classList.add(c))
    }
  }

  _syncCells() {
    this.iconCellTargets.forEach((c) => {
      const on = c.dataset.icon === this._icon
      c.classList.toggle("border-primary", on)
      c.classList.toggle("bg-primary/12", on)
      c.classList.toggle("text-primary", on)
      c.classList.toggle("text-muted-foreground", !on)
    })
  }

  _syncSwatches() {
    this.swatchTargets.forEach((s) => {
      const on = (s.dataset.color || "").toLowerCase() === (this._color || "").toLowerCase()
      s.classList.toggle("ring-2", on)
      s.classList.toggle("ring-primary", on)
      s.classList.toggle("ring-offset-2", on)
      s.classList.toggle("ring-offset-background", on)
    })
  }

  _isIncome() {
    if (!this.hasKindInputTarget) return false
    const checked = this.kindInputTargets.find((r) => r.checked)
    return checked ? checked.value === "income" : false
  }

  _cellFor(name) {
    return this.iconCellTargets.find((c) => c.dataset.icon === name) || this.iconCellTargets[0]
  }

  // Ícone inicial = o do radio CHECADO na grade (no editar, a categoria já tem
  // ícone; usar a 1ª célula marcava o item errado). Fallback: primeira célula.
  _checkedIconName() {
    const cell = this.iconCellTargets.find((c) => {
      const r = c.querySelector("input[type=radio]")
      return r && r.checked
    })
    return cell ? cell.dataset.icon : this._firstIconName()
  }

  _firstIconName() {
    return this.hasIconCellTarget ? this.iconCellTargets[0].dataset.icon : "shopping-bag"
  }
}
